import {chunk} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import HelpButton from 'help/HelpButton';
import {
  FONT_PROXIMA_11,
  FONT_PROXIMA_12,
  FONT_PROXIMA_13,
  FONT_PROXIMA_14,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {GRAY_DARKER} from 'ui-library/Colors';
import {FloatingPlacement, FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';

export interface StepConfigField {
  variableName?: string;
  addIcon?: JSX.Element;
  editor: JSX.Element;
  infoText?: string;
}
interface StepConfigFieldsTemplateProps {
  sectionName?: string;
  fields: Array<StepConfigField>;
  numColumns: number;
  isCompact?: boolean;
  sectionInfoText?: string;
}

function StepConfigFieldsHelpButtonOrNull({
  infoText,
  isCompact,
}: {
  infoText?: string;
  isCompact: boolean;
}): JSX.Element | null {
  if (infoText != null) {
    return (
      <HelpButton
        renderContent={() => <span className="u-pre-wrap u-normal-font-weight">{infoText}</span>}
        iconClassName={isCompact ? 'u-mini-spacing-left' : 'u-small-spacing-left'}
        placement={FloatingPlacement.TOP}
        interaction={FloatingInteraction.HOVER}
      />
    );
  }
  return null;
}

/** @package */
export function VariableHeaderOrNull({
  variableName,
  infoText,
  addIcon,
  isCompact,
}: {
  variableName?: string;
  infoText?: string;
  addIcon?: JSX.Element;
  isCompact: boolean;
}): JSX.Element | null {
  if (variableName == null) {
    return null;
  }
  return (
    <div className={css(stepConfigFieldsTemplateStyles.variableHeader)}>
      <span
        className={css(
          !isCompact && stepConfigFieldsTemplateStyles.variableText,
          isCompact && stepConfigFieldsTemplateStyles.compactVariableText
        )}
      >
        {variableName}
      </span>
      <StepConfigFieldsHelpButtonOrNull infoText={infoText} isCompact={isCompact} />
      {addIcon}
    </div>
  );
}

/** @package */
export function SectionNameOrNull({
  sectionName,
  isCompact,
  infoText,
}: {
  sectionName?: string;
  isCompact: boolean;
  infoText?: string;
}): JSX.Element | null {
  if (sectionName == null && infoText == null) {
    return null;
  }
  return (
    <Col sm={12}>
      <div
        className={css(
          stepConfigFieldsTemplateStyles.sharedSectionName,
          isCompact && stepConfigFieldsTemplateStyles.compactSectionName,
          !isCompact && stepConfigFieldsTemplateStyles.sectionName
        )}
      >
        {sectionName}
        <StepConfigFieldsHelpButtonOrNull infoText={infoText} isCompact={isCompact} />
      </div>
    </Col>
  );
}

// TODO(BNCH-75415): Use this component to render taggedUnion editors.
export default function StepConfigFieldsTemplate({
  sectionName,
  fields,
  numColumns,
  isCompact = false,
  sectionInfoText = undefined,
}: StepConfigFieldsTemplateProps): JSX.Element {
  return (
    <>
      {[...chunk(fields, numColumns)].map((rowFields, rowIndex) => (
        <Row key={rowIndex} className={css(!isCompact && stepConfigFieldsTemplateStyles.marginBottom)}>
          {rowIndex === 0 && (
            <SectionNameOrNull sectionName={sectionName} isCompact={isCompact} infoText={sectionInfoText} />
          )}
          {rowFields.map(({variableName, infoText, addIcon, editor}, colIndex) => (
            <Col
              key={colIndex}
              sm={12 / numColumns}
              // For two-column displays, decrease the amount of padding between the two columns by reducing
              // the padding on the right of the first column and the padding on the left of the second column
              className={css(
                numColumns === 2 && colIndex % 2 === 0 && stepConfigFieldsTemplateStyles.smallPaddingRight,
                numColumns === 2 && colIndex % 2 === 1 && stepConfigFieldsTemplateStyles.smallPaddingLeft
              )}
            >
              <VariableHeaderOrNull
                variableName={variableName}
                infoText={infoText}
                addIcon={addIcon}
                isCompact={isCompact}
              />
              {editor}
            </Col>
          ))}
        </Row>
      ))}
    </>
  );
}

/** @package */
export const stepConfigFieldsTemplateStyles = StyleSheet.create({
  variableHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 4,
  },
  variableText: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  compactVariableText: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  marginBottom: {
    marginBottom: 8,
  },
  smallPaddingRight: {
    paddingRight: 8,
  },
  smallPaddingLeft: {
    paddingLeft: 8,
  },
  sharedSectionName: {
    color: GRAY_DARKER,
    textTransform: 'uppercase',
  },
  sectionName: {
    ...FONT_PROXIMA_11,
    marginBottom: 10,
  },
  compactSectionName: {
    ...FONT_PROXIMA_12,
    marginBottom: 4,
  },
});
